/**
 * Socket.IO Client Context
 * 
 * Provides real-time communication throughout the React app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { getUser } from './auth';

const SocketContext = createContext(null);

// Get backend URL from environment
// Strip /api suffix since Socket.IO connects to root, not /api namespace
const _rawBackendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BACKEND_URL = _rawBackendUrl.replace(/\/api\/?$/, '');

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        // Connect socket for ALL users (including guests) so they can receive
        // real-time slot availability / processing events when viewing a turf.
        // Authentication event is only sent if user is logged in (for owner dashboard).
        const user = getUser();

        console.log('🔌 Initializing Socket.IO client...');


        // Create socket connection with performance optimizations
        const newSocket = io(BACKEND_URL, {
            withCredentials: true,
            // PERFORMANCE: Force WebSocket transport to avoid HTTP polling requests
            transports: ['websocket'],
            // PERFORMANCE: Exponential backoff to prevent server hammering
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: Infinity,
            // PERFORMANCE: Enable connection state recovery (v4.6+)
            // This restores missed packets upon reconnection
            connectionStateRecovery: {
                maxDisconnectionDuration: 2 * 60 * 1000, // 2 minutes
                skipMiddlewares: true,
            }
        });

        // Connection event handlers
        newSocket.on('connect', () => {
            console.log('✅ Socket connected:', newSocket.id);
            setConnected(true);

            // Only send authenticate event for logged-in users
            // Guests connect too (for slotProcessing events) but don't authenticate
            if (user) {
                newSocket.emit('authenticate', {
                    userId: user._id,
                    role: user.role
                });
            }
        });

        newSocket.on('authenticated', (data) => {
            console.log('🔐 Socket authenticated:', data);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('❌ Socket disconnected:', reason);
            setConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('🔴 Socket connection error:', error.message);
        });

        newSocket.on('reconnect', (attemptNumber) => {
            console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
        });

        setSocket(newSocket);

        // Cleanup on unmount
        return () => {
            console.log('🔌 Disconnecting socket...');
            newSocket.disconnect();
        };
    }, []); // Empty dependency array - only run once on mount

    return (
        <SocketContext.Provider value={{ socket, connected }}>
            {children}
        </SocketContext.Provider>
    );
};

// Custom hook to use socket
export const useSocket = () => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

// Custom hook for turf viewing
export const useTurfSocket = (turfId) => {
    const { socket, connected } = useSocket();
    const [slotUpdates, setSlotUpdates] = useState([]);
    // Map of "date-time" -> { expiresAt: ISO string } for slots currently being processed
    const [processingSlots, setProcessingSlots] = useState({});

    useEffect(() => {
        if (!socket || !connected || !turfId) return;

        console.log(`👁️ Subscribing to turf updates: ${turfId}`);

        // Join turf room
        socket.emit('viewTurf', turfId);

        // Listen for slot updates
        const handleSlotBooked = (data) => {
            console.log('📢 Slot booked:', data);
            setSlotUpdates(prev => [...prev, { type: 'booked', ...data }]);
            // Remove from processing when confirmed booked
            if (data.slot) {
                const key = `${data.slot.date}-${data.slot.time}`;
                setProcessingSlots(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            }
        };

        const handleSlotCancelled = (data) => {
            console.log('📢 Slot cancelled:', data);
            setSlotUpdates(prev => [...prev, { type: 'cancelled', ...data }]);
        };

        const handleSlotsBlocked = (data) => {
            console.log('📢 Slots blocked:', data);
            setSlotUpdates(prev => [...prev, { type: 'blocked', ...data }]);
        };

        const handleSlotsUnblocked = (data) => {
            console.log('📢 Slots unblocked:', data);
            setSlotUpdates(prev => [...prev, { type: 'unblocked', ...data }]);
        };

        // ─── NEW: Slot processing handlers ─────────────────────────────────────
        // Fired when another user clicks "Pay Now" — show the slot as "Processing..."
        const handleSlotProcessing = (data) => {
            console.log('⏳ Slot processing:', data);
            setSlotUpdates(prev => [...prev, { type: 'processing', ...data }]);

            // Mark each slot as processing with an expiry timestamp
            if (data.slots && Array.isArray(data.slots)) {
                setProcessingSlots(prev => {
                    const next = { ...prev };
                    data.slots.forEach(slot => {
                        const key = `${slot.date}-${slot.time}`;
                        next[key] = { expiresAt: data.expiresAt };
                    });
                    return next;
                });

                // Auto-remove processing state after TTL expires
                const ttlMs = data.expiresAt
                    ? new Date(data.expiresAt).getTime() - Date.now()
                    : 60000;
                if (ttlMs > 0) {
                    setTimeout(() => {
                        setProcessingSlots(prev => {
                            const next = { ...prev };
                            data.slots.forEach(slot => {
                                delete next[`${slot.date}-${slot.time}`];
                            });
                            return next;
                        });
                    }, ttlMs + 500); // +500ms grace period
                }
            }
        };

        // Fired when a reservation expires or is released — show slot as "Available" again
        const handleSlotAvailable = (data) => {
            console.log('✅ Slot available again:', data);
            setSlotUpdates(prev => [...prev, { type: 'available', ...data }]);

            // Remove processing state for these slots
            if (data.slots && Array.isArray(data.slots)) {
                setProcessingSlots(prev => {
                    const next = { ...prev };
                    data.slots.forEach(slot => {
                        delete next[`${slot.date}-${slot.time}`];
                    });
                    return next;
                });
            }
        };

        socket.on('slotBooked', handleSlotBooked);
        socket.on('slotCancelled', handleSlotCancelled);
        socket.on('slotsBlocked', handleSlotsBlocked);
        socket.on('slotsUnblocked', handleSlotsUnblocked);
        socket.on('slotProcessing', handleSlotProcessing);
        socket.on('slotAvailable', handleSlotAvailable);

        // Cleanup
        return () => {
            console.log(`👋 Unsubscribing from turf updates: ${turfId}`);
            socket.emit('leaveTurf', turfId);
            socket.off('slotBooked', handleSlotBooked);
            socket.off('slotCancelled', handleSlotCancelled);
            socket.off('slotsBlocked', handleSlotsBlocked);
            socket.off('slotsUnblocked', handleSlotsUnblocked);
            socket.off('slotProcessing', handleSlotProcessing);
            socket.off('slotAvailable', handleSlotAvailable);
        };
    }, [socket, connected, turfId]);

    return {
        slotUpdates,
        processingSlots, // { "2025-01-15-09:00": { expiresAt: "..." }, ... }
        clearUpdates: () => setSlotUpdates([])
    };
};


// Custom hook for owner dashboard
export const useOwnerSocket = (ownerId) => {
    const { socket, connected } = useSocket();
    const [bookingNotifications, setBookingNotifications] = useState([]);

    useEffect(() => {
        if (!socket || !connected || !ownerId) return;

        console.log(`📊 Subscribing to owner dashboard: ${ownerId}`);

        // Subscribe to dashboard updates
        socket.emit('subscribeDashboard', ownerId);

        // Listen for booking notifications
        const handleNewBooking = (data) => {
            console.log('📢 New booking notification:', data);
            setBookingNotifications(prev => [...prev, { type: 'new', ...data }]);
        };

        const handleBookingCancelled = (data) => {
            console.log('📢 Booking cancelled notification:', data);
            setBookingNotifications(prev => [...prev, { type: 'cancelled', ...data }]);
        };

        socket.on('newBooking', handleNewBooking);
        socket.on('bookingCancelled', handleBookingCancelled);

        // Cleanup
        return () => {
            socket.off('newBooking', handleNewBooking);
            socket.off('bookingCancelled', handleBookingCancelled);
        };
    }, [socket, connected, ownerId]);

    return { bookingNotifications, clearNotifications: () => setBookingNotifications([]) };
};

export default SocketContext;
